import React, {useContext, useEffect, useState} from "react";
import {AiTierServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, Descriptions, Form, Input, InputNumber, message, Modal, Skeleton, Spin} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import * as globalStyles from "../App.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import {DocumentTitle} from "../DocumentTitle";
import AiTier from "../../domain/AiTier";
import {useForm} from "antd/es/form/Form";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import CreditsValue from "../common/CreditsValue";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function AiTierDetail() {
    const appContext = useContext(AppContextContext);
    const aiTierService = useContext(AiTierServiceContext);
    const navigate = useNavigate();
    const {aiTierId} = useParams<{ aiTierId: string }>();
    const [aiTier, setAiTier] = useState<AiTier | undefined>();
    const [editMode, setEditMode] = useState(false);
    const [form] = useForm();

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    const title = aiTierId ? (aiTier?.name || "AI tier") : "New AI tier";

    useEffect(() => {
        if (aiTierId) {
            reload();
        } else {
            setEditMode(true);
        }
    }, [aiTierId]);

    if (aiTierId && !aiTier) {
        return <Skeleton/>;
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/definitions/ai-tiers"}>AI tiers</Link>},
                                {title}
                            ]}
                />

                <Spin spinning={aiTierId ? !aiTier : false} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{title}</h1>

                    {aiTierId &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button type={"primary"} onClick={() => setEditMode(!editMode)}>Edit</Button>
                            <Button
                                danger
                                style={{marginLeft: 8}}
                                onClick={() => {
                                    Modal.confirm({
                                        content: "Do you really want to delete this AI tier?",
                                        okText: "Delete",
                                        cancelText: "Cancel",
                                        okButtonProps: {danger: true},
                                        onOk: () => onDelete()
                                    });
                                }}
                            >
                                Delete
                            </Button>
                        </div>
                    }

                    <Collapse isOpen={!aiTierId || editMode}>
                        {aiTierId && <h3>Edit AI tier</h3>}

                        <Form
                            {...layout}
                            form={form}
                            className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`}
                            onFinish={onFinish}
                        >
                            <Form.Item
                                name="name"
                                label="Name"
                                rules={[
                                    {required: true, message: "Name is required."},
                                    {validator: serverViolationsHolder.createServerValidator('UNIQUE', 'name', undefined, {compareLowerCaseValues: true}), message: "Name is already used."}
                                ]}
                            >
                                <Input maxLength={100} autoFocus={!aiTierId}/>
                            </Form.Item>

                            <Form.Item
                                name="maxCreditsPerMonth"
                                label="Max credits / month"
                                rules={[{required: true, message: "Max credits per month is required."}]}
                            >
                                <InputNumber min={0} step={0.01} style={{width: "100%"}}/>
                            </Form.Item>

                            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                                <Button type={"primary"} htmlType={"submit"}>Save</Button>
                                <Button onClick={() => aiTierId ? setEditMode(false) : navigate("/definitions/ai-tiers")}>Cancel</Button>
                            </Form.Item>
                        </Form>
                    </Collapse>

                    {aiTierId && aiTier && (
                        <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                            <Descriptions.Item label="Name">{aiTier.name}</Descriptions.Item>
                            <Descriptions.Item label="Max credits / month"><CreditsValue value={aiTier.maxCreditsPerMonth} compact={true}/></Descriptions.Item>
                        </Descriptions>
                    )}
                </Spin>
            </>
        </DocumentTitle>
    );

    function onFinish(values: any) {
        if (aiTierId) {
            aiTierService.update(aiTierId, values)
                .then(
                    () => {
                        message.success(<>AI tier <b>{values.name}</b> successfully updated.</>);
                        setEditMode(false);
                        reload();
                    },
                    error => serverViolationsHolder.handleServerError(error, form)
                );
        } else {
            aiTierService.add(values)
                .then(
                    () => {
                        message.success(<>AI tier <b>{values.name}</b> successfully added.</>);
                        navigate("/definitions/ai-tiers");
                    },
                    error => serverViolationsHolder.handleServerError(error, form)
                );
        }
    }

    function onDelete() {
        aiTierService.delete(aiTier!)
            .then(() => {
                message.success(<>AI tier <b>{aiTier!.name}</b> deleted.</>);
                navigate("/definitions/ai-tiers");
            });
    }

    function reload() {
        aiTierService.get(aiTierId!).then(result => {
            setAiTier(result);
            form.setFieldsValue(result);
        });
    }
}

export default AiTierDetail;
